import { NavLink, Outlet, useLocation } from "react-router-dom";

import {
  authAction,
  primaryNavigation,
  secondaryNavigation,
} from "@/shared/config/navigation";
import { useAuthSession } from "@/shared/auth/useAuthSession";
import { useI18n } from "@/shared/i18n/LanguageContext";
import { SiteNoticeBar } from "@/blocks/site-notice/SiteNoticeBar";

function linkClassName(isActive: boolean) {
  return isActive ? "nav-link nav-link--active" : "nav-link";
}

export function AppShell() {
  const authSession = useAuthSession();
  const { copy, language, toggleLanguage } = useI18n();
  const location = useLocation();
  const shellClassName = location.pathname.startsWith("/analysis")
    ? "app-shell app-shell--analysis"
    : location.pathname.startsWith("/about")
      ? "app-shell app-shell--about"
      : "app-shell";

  return (
    <div className={shellClassName}>
      <header className="app-header">
        <div className="app-header__inner">
          <NavLink to="/" className="brand" aria-label="CatieChess home">
            <span className="brand__mark">CATIECHESS</span>
            {copy.shell.tagline ? (
              <span className="brand__tagline">{copy.shell.tagline}</span>
            ) : null}
          </NavLink>

          <nav className="app-nav" aria-label="Primary navigation">
            {primaryNavigation.map((item) => {
              const label =
                item.kind === "route" && item.to === "/play"
                  ? copy.shell.nav.play
                  : item.kind === "route" && item.to === "/analysis"
                    ? copy.shell.nav.analysis
                    : item.kind === "route" && item.to === "/livestream"
                      ? copy.shell.nav.livestream
                      : item.label;
              return item.kind === "route" ? (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => linkClassName(isActive)}
                >
                  {label}
                </NavLink>
              ) : (
                <a key={item.href} href={item.href} className="nav-link">
                  {label}
                </a>
              );
            })}
            {secondaryNavigation.map((item) => {
              const label =
                item.kind === "route" && item.to === "/about"
                  ? copy.shell.nav.about
                  : item.kind === "route" && item.to === "/devlog"
                    ? copy.shell.nav.devlog
                  : item.label;
              return item.kind === "route" ? (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) => linkClassName(isActive)}
                >
                  {label}
                </NavLink>
              ) : (
                <a key={item.href} href={item.href} className="nav-link">
                  {label}
                </a>
              );
            })}
          </nav>

          <div className="app-header__actions">
            <button
              className="language-toggle"
              type="button"
              onClick={toggleLanguage}
              aria-label={copy.shell.switchLabel}
            >
              <span data-active={language === "en"}>EN</span>
              <span data-active={language === "zh"}>中</span>
            </button>

            {authSession.username ? (
              <NavLink
                to={`/@/${authSession.username}`}
                className="nav-action"
              >
                @{authSession.username}
              </NavLink>
            ) : authSession.status === "checking" ? (
              <span className="nav-action nav-action--static">{copy.shell.checking}</span>
            ) : (
              <NavLink to={authAction.to} className="nav-action">
                {copy.shell.signUp}
              </NavLink>
            )}
          </div>
        </div>
      </header>

      <SiteNoticeBar />

      <main className="app-main">
        <Outlet />
      </main>
    </div>
  );
}
